/**
 * useWorkspaceMembers — canonical cached workspace-member hook
 * (`.tasks/service-cache/plan.md`, BUG-001).
 *
 * Reads the member roster for one workspace through the shared service cache
 * (`useCachedCall`), so every consumer of the same workspace shares a single
 * fetch per session — events and the 24h TTL keep it fresh (decisions 2, 13).
 *
 * The fetcher throws on a failed `Result` (decision 6) so failures are never
 * cached as fresh data; the cache key is BARE (`members:${wsId}`) — the cache
 * hook applies the mandatory `${userId}:` scope (decision 4).
 */

import {
  WORKSPACE_LIBRARY_SERVICE_IDENT,
  type IWorkspaceLibraryService,
  type WorkspaceMember,
} from '@bloomstack/apis/workspace';
import { useService } from './useService';
import { useCachedCall } from './useCachedCalls';

/** Prefix shared by every member cache key — invalidation and readers use it. */
export const MEMBERS_KEY_PREFIX = 'members:';

/** Bare (unscoped) cache key for a workspace's member roster. */
export function membersKey(wsId: string): string {
  return `${MEMBERS_KEY_PREFIX}${wsId}`;
}

/** Events are the source of truth; the TTL only covers downtime (decision 13). */
export const MEMBERS_TTL_MS = 24 * 60 * 60 * 1000;

/** The resolved `queryMembers` success payload (the `QueryResult` body). */
export type WorkspaceMembersPayload = {
  results: WorkspaceMember[];
};

/** A member projected for pickers / lists. */
export type WorkspaceMemberOption = {
  /** The `UserProfile` id — the wire value for assignment. */
  profileId: string;
  /** The `User` id — the addressing unit, NOT `Task.assignedUserId`. */
  userId: string | null;
  name: string;
  avatar?: string;
  displayType?: string;
};

/** Map a `queryMembers` success payload into sorted options. */
export function toMemberOptions(payload: WorkspaceMembersPayload): WorkspaceMemberOption[] {
  return payload.results
    .map((m) => ({
      profileId: m.userProfileId,
      userId: m.userProfile?.userId ?? null,
      name: m.userProfile?.name ?? 'Unknown',
      avatar: m.userProfile?.avatar,
      displayType: m.userProfile?.displayType,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The member-fetch surface (kept narrow so callers pass only what they use). */
type MembersService = Pick<IWorkspaceLibraryService, 'queryMembers'>;

/**
 * The one Result-translating member fetcher, shared by every member consumer
 * (this hook, `useWorkspaceMembersMap`, `AssigneeFilterSelect`) so `queryMembers`
 * is called from exactly one place. Throws on failure (decision 6) — a failed
 * fetch is never cached.
 */
export function makeMembersFetcher(
  service: MembersService,
  wsId: string,
): () => Promise<WorkspaceMembersPayload> {
  return async () => {
    const res = await service.queryMembers(wsId, {}, { limit: 1000 });
    if (!res.success) throw res.error;
    return res.result;
  };
}

export type UseWorkspaceMembersResult = {
  /** Sorted member options, or `null` until the roster is cached. */
  members: WorkspaceMemberOption[] | null;
  loading: boolean;
  refreshing: boolean;
  error: unknown;
};

/**
 * Cached member roster for one workspace. `null` members means "not loaded
 * yet"; a cached roster is served immediately while a stale/expired one
 * revalidates behind it.
 */
export function useWorkspaceMembers(wsId: string): UseWorkspaceMembersResult {
  // Global tier is the default pool behavior — no `.withPriority`. BUG-001's
  // flood was repetition, not priority (task 003).
  const workspaceService = useService<IWorkspaceLibraryService>(WORKSPACE_LIBRARY_SERVICE_IDENT);

  const state = useCachedCall(
    makeMembersFetcher(workspaceService, wsId),
    membersKey(wsId),
    { ttlMs: MEMBERS_TTL_MS },
  );

  return {
    members: state.data == null ? null : toMemberOptions(state.data as WorkspaceMembersPayload),
    loading: state.loading,
    refreshing: state.refreshing,
    error: state.error,
  };
}
